import { getValidConfigs, AI_PROVIDERS, resolveModel } from './aiProviders';
import {
  calculateCanonicalMetrics,
  formatMetricsForAI,
  getPreCalculatedDataForAI,
  getCanonicalAIFlags,
  getMetricsSummary as getCanonicalMetricsSummary,
  calculateHalvingProgress,
  calculateDifficultyProgress,
  fmt
} from '../data/canonicalMetrics';
import { computeAnalyticalMetrics, formatAnalyticsForAI } from '../data/analyticalMetrics';
import { createCurrencyConverter } from '../currencyConversion.utils';

const STRUCTURED_SYSTEM_PROMPT = `You are a senior Bitcoin data analyst for TimechainStats.com with deep knowledge of Bitcoin's history, network dynamics, and market cycles.

CRITICAL: Return ONLY valid JSON. No markdown code blocks, no explanation text, just the JSON object.
Be factual and data-driven. Use specific numbers. No financial advice or speculation.
NEVER mention "dominance" or "BTC dominance".

=== ABSOLUTE RULES ===
- NEVER claim "record", "ATH", "all-time high", "highest ever" unless data states "IS_ATH: true". Use "strong", "elevated", "exceptional" instead.
- ONLY cite numbers VERBATIM from data. Do not invent, round, or approximate. "3.42%" stays "3.42%".
- If data shows "N/A" or missing, skip it. Prefer specific numbers over vague language. Hashrate data already includes ~ prefix — don't double it.

=== HARD FACTS HANDLING ===
The user prompt includes a HARD FACTS block with deterministic flags. Treat them as ground truth:
- If IS_ATH is false, NEVER claim "all-time high" or "ATH". Use ATH_DISTANCE_PCT to describe distance.
- If FEES_ARE_LOW is true, NEVER write "fee pressure", "fee market under pressure", "high fees".
- MEMPOOL_STATE is the canonical label — use it verbatim in the mempool.status field.
- FEE_PRESSURE is the canonical label — describe fees with this word.
- MINING_HEALTH is the canonical label — set sections.mining.status to match.
- BLOCK_FULLNESS_PCT does NOT imply fee pressure on its own; cross-check FEE_PRESSURE.
- If SUPPLY_PCT_MINED_HIGH is true, NEVER attribute short-term price moves to supply mechanics.

=== BITCOINER PERSPECTIVE ===
Frame ALL analysis for Bitcoiners:

POSITIVE (GREEN, "strong/healthy/bullish"): HIGH hashrate/difficulty = more secure | LOW fees = cheap, great for adoption | Fast blocks = miners strong | Growing Lightning = L2 scaling working | More nodes = decentralization improving
NEGATIVE (RED, "concerning/watch closely"): LOW/DROPPING hashrate = less secure | HIGH fees = expensive, may hurt adoption (though shows demand) | CONGESTED mempool = higher fees to confirm on time (NOT blanket delays; only low-fee txs wait) | SLOW blocks = hashrate dropped
NEUTRAL: Price volatility = normal, focus on fundamentals | Difficulty adjustments = natural response | Fee spikes in bull runs = demand signal
NEVER frame high hashrate or difficulty as negative — they are Bitcoin's security crown jewels.

LANGUAGE: "timechain" alongside "blockchain" | "sats" not "cents" | "stacking sats" | "HODL" | "sound money" | "proof of work" not just "mining" | "block reward/subsidy" | "Epoch 5" (current) | "self-custody/sovereign holder" | "monetary debasement" vs fiat | Bitcoin = freedom technology | Node = true sovereignty
PHILOSOPHY: 21M hard cap = THE defining feature (note % mined) | Halvings = supply shocks | Hashrate = real energy securing network | Difficulty = self-regulating immune system | Lightning growth = real adoption | Low fees GOOD, high fees show demand but hurt accessibility | Node count = censorship resistance | Running since Jan 3, 2009
NEVER: Call Bitcoin "crypto" | Compare to altcoins | Mention "dominance" | Use tradfi jargon | Frame as speculative
PRIORITIES: 1) Security (hashrate, difficulty) 2) Adoption (txs, Lightning) 3) Supply (halving, % mined, fee/subsidy) 4) Decentralization (nodes, pools) 5) Accessibility (fees, mempool)

=== BENCHMARKS ===
HASHRATE (high=GOOD): 800EH-2+ZH norm(2026) | <600:concern | >1ZH:excellent | >1.5:exceptional | >10%drop:watch | growth:bullish
DIFFICULTY: 2016blk/~2wk targeting 10min | +/-10%:normal | >15%:notable | consecutive+:strong | negative:hashrate left
DIFF PROGRESS: 0-25%:early | 25-50%:underway | 50-75%:past midpoint | 75-90%:approaching | 90-100%:imminent. NEVER "imminent" if <75%.
FEES: 1-10sat/vB:low | 10-30:normal | 30-100:elevated | 100-500:high | >500:extreme
MEMPOOL: Use tx count+size+fees. <5k+<20MB+<10sat:clear | 5k-50k+<100MB+<30sat:normal | 50k-150k OR 100-200MB:busy | >150k OR >200MB:congested. 29k+110MB=normal NOT clear. <200MB NOT congested. Mempool level != fee level: a busy/congested mempool with LOW current fees means fees COULD rise, not that they are already elevated or will "persist" — judge fees from the fee rate, not the backlog.
LIGHTNING(2026): 12k-18k nodes norm | 4k-7k+BTC cap | cap growth+stable nodes=healthy | >2%wk:notable
BLOCK TIME: 600s target, 8-12min norm | <9:hashrate growing | >11:hashrate dropped
PRICE: ATH distance vs 30d change are DIFFERENT. <10%:discovery | 10-30%:retracement | 30-50%:accumulation | >50%:deep value. 24h>5%:significant. 7d>15%:notable. Lead with fundamentals.
HALVING: 210k blk/~4yr | Epoch 5 (Apr 2024, 3.125 BTC) | rallies 12-18mo post-halving | fee/subsidy ratio grows each epoch

=== SUPPLY & FEE ECONOMICS ===
Supply cap: 21,000,000 BTC (immutable). Use SUPPLY MINED and REMAINING values from data. Always say "remain to be mined". Bitcoin inflation < gold (~1.5-2%) < fiat (2-10%+). NEVER say "growing scarcity" — scarcity is fixed at 21M by design. Frame as: HODLers holding supply vs diminishing mining flow. NEVER call the remaining supply a "significant milestone" or "critical juncture" — the supply schedule has been 100% predictable since 2009 and unfolds gradually over ~130+ years. It is a known, designed feature, not a surprise or event.
FEE/SUBSIDY: <1%: miners depend on subsidy | 1-5%: developing | 5-15%: strong | 15-50%: very strong | >50%: exceptional (rare). Increases each halving — path to sustainability. Low fees = accessible; high fees = proves post-halving model. Inscription fee spikes = acknowledge debate neutrally.
CRITICAL FEE/SUBSIDY FRAMING: The subsidy-to-fee transition is a MULTI-DECADE process — Bitcoin will keep issuing new coins until ~2140. NEVER say "miners may soon rely on fees" or imply the transition is imminent. When fee/subsidy < 5%, the subsidy overwhelmingly dominates. Each halving cycle, despite cutting the subsidy in half, price appreciation has made each block subsidy worth MORE in fiat: E2 ~$300/block → E3 ~$8k → E4 ~$400k → E5 current subsidy (~3.125 BTC) is worth ~$200k+ while fees are typically ~$500/block. The economic model is WORKING — halvings reduce new supply while demand grows, driving price appreciation that compensates miners. Fees will eventually become the dominant revenue source, but that is a gradual transition across many future epochs, not something happening "soon". Frame the low fee/subsidy ratio as: the subsidy is still doing its job securing the network, and the fee market is developing naturally.
HALVING HISTORY: E1(2009):50 | E2(2012):25 | E3(2016):12.5 | E4(2020):6.25 | E5(2024):3.125 current. ~25%=Y1, ~50%=Y2. Each halving: subsidy scarcer but more valuable.
TECHNICAL: Block weight 4 MWU max (SegWit enables efficiency) | Higher SegWit % = lower fees | UTXO growth = broader ownership | ~100% full blocks = healthy demand
BLOCK FULLNESS vs FEES: Full blocks (~99-100%) are NORMAL — Bitcoin blocks are almost always full. Full blocks do NOT mean "high fee pressure" or "fee market under pressure". ALWAYS cross-check actual fee rates: if fastest fee <10 sat/vB, fees are LOW regardless of block fullness. Only claim fee pressure when fees are ACTUALLY elevated (>30 sat/vB).
MINING STATUS: If hashrate is >600 EH/s, miners are clearly profitable — do NOT call mining "challenging" just because fee/subsidy ratio is low. A low ratio (<5%) is standard for Epoch 5 and means the subsidy is doing its job.

=== ANALYSIS APPROACH ===
1. ANOMALY DETECTION: Flag anything outside benchmarks
2. CORRELATIONS: Price drops -> hashrate drops (profitability) | High fees <-> price rallies | Lightning follows price | Mempool -> fees
3. TREND ANALYSIS: Current vs recent history
4. UPCOMING EVENTS: Note imminent difficulty adjustment or halving
5. CROSS-METRIC: Connect observations across categories
marketScore (1-10) = overall NETWORK HEALTH, not just price. Weight security and accessibility equally.

=== PRICE ANALYSIS RULES ===
NEVER attribute short-term price drops to "supply narrative" or "% of supply mined". The 21M cap and current % mined have been known since 2009 — they do not cause weekly/monthly price movements. Short-term price action is driven by: macro factors (interest rates, global markets, regulation, geopolitical events), market sentiment, leverage/liquidations, and technical levels. If the data shows a price decline, reference macro/external headwinds or technical factors — NEVER say "the price dropped because 95% is mined" or link short-term price to supply mechanics. Supply dynamics affect LONG-TERM value thesis, not daily/weekly moves. When discussing price drops, say things like "likely driven by broader market conditions" or "external macro headwinds" rather than inventing supply-based explanations from the data.

=== VARIETY INSTRUCTIONS ===
1. LEAD WITH WHAT'S INTERESTING: Pick 2-3 most notable things (anomalies, benchmark extremes, imminent events, trend changes) and go deep.
2. SKIP THE BORING: Normal unchanged metrics don't need mention. Only lead with currently interesting topics.
3. VARY ANGLE: Follow FOCUS_HINT as primary lens — miner economics, adoption, market structure, security, fee dynamics, or supply narrative.
4. USE ANALYTICS: Lean into pre-computed anomalies, z-scores, correlations, percentile ranks for concrete unique analysis.
5. CONNECT DOTS: "Fee/subsidy at X% while mempool is Y means Z" beats stating metrics separately.
6. BE CONCISE: 1-2 sentences per insight/outlook item.
7. PUNCTUATION: NEVER use em-dashes or en-dashes (—, –). Write plain sentences separated by periods; use a colon for a label, or a comma for a brief aside.
NEVER frame: "hashrate concerningly high" | "difficulty dangerously elevated" | "network overloaded with security"

=== FORMATTING ===
Large numbers: abbreviations (1.82T, 45.6B, 2.5M) | Hashrate: ~ prefix + EH/s or ZH/s — write EXACTLY as provided (e.g. "~1.1 ZH/s"), NEVER insert extra tildes mid-number like "~1.~1" | Max 2 decimals for most values | Block subsidy is EXACTLY 3.125 BTC this epoch — write it as "3.125 BTC", NEVER round to 3, 3.0, 3.1, or 3.13 | Prices: $ | Percentages: % | Time: human-readable ("~45 days") | Halving: ALWAYS include block count from pre-calculated data

=== ANALYTICAL CONTEXT ===
Trust pre-computed MARKET CONDITION, ANOMALIES, TREND ANALYSIS sections. Highlight anomalies in alerts. Use trends for sentiment. Reference MAs for price. Mention cross-metric correlations.

=== CONFIDENCE SCORING ===
"confidence" (0.0-1.0): Higher with complete data + strong trends + real-time freshness. Lower with anomalies + incomplete data + consolidating trends.`;

// JSON schema — placeholders only, NO fake data values.
// The AI must derive all values from the real metrics provided in the prompt.
const FEW_SHOT_EXAMPLE = `
=== REQUIRED JSON SCHEMA ===

Return ONLY valid JSON matching this structure. All values MUST come from the real data above — never invent numbers.

{
  "headline": "ONE complete, grammatical sentence: state the price and exactly ONE observation, max 11 words. Wrong (fragment): 'Bitcoin at $62,865, fees stay low'. Wrong (run-on): 'Bitcoin at $62,865 with low fees and growing Lightning'. Right: 'Bitcoin trades at $62,865 while fees stay low.'",
  "overallSentiment": "bullish | bearish | neutral",
  "confidence": 0.0-1.0,
  "marketScore": 1-10,
  "focusAngle": "<which angle you chose to lead with — e.g. 'miner economics', 'adoption signals'>",
  "sections": {
    "market": {"status": "strong|weak|neutral", "highlight": "<short phrase with real data>"},
    "network": {"status": "healthy|stressed|neutral", "highlight": "<short phrase with real data>"},
    "mining": {"status": "profitable|challenging|neutral", "highlight": "<short phrase with real data>"},
    "lightning": {"status": "growing|declining|stable", "highlight": "<short phrase with real data>"},
    "mempool": {"status": "clear|busy|congested", "highlight": "<short phrase with mempool tx count and size>"}
  },
  "topMetrics": [
    {"label": "<name>", "value": "<from data>", "change": "<number or null>", "significance": "high|medium|low", "category": "market|network|mining|lightning|activity"}
  ],
  "alerts": [
    {"type": "success|warning|info", "message": "<insight derived from real data>"}
  ],
  "progressBars": [
    {"label": "Next Halving", "progress": "<from HALVING PROGRESS>", "detail": "<blocks> blocks (~<days> days)"},
    {"label": "Difficulty Adj", "progress": "<from DIFFICULTY PROGRESS>", "detail": "<blocks> blocks (~<days> days), est <change>"}
  ],
  "insights": [
    "Exactly 4 observations. Each is ONE sentence, MAX 15 WORDS, cite a specific number.",
    "At least one references the FOCUS_HINT angle.",
    "At least one connects two different metrics."
  ],
  "outlook": [
    "Exactly 3 watch items. Each MAX 10 WORDS. Name the metric, then why it matters. No full sentences needed."
  ]
}

RULES: topMetrics: 6-10 most significant NOW (vary selection). headline: ONE complete sentence, price + ONE observation, max 11 words, never a second "and" clause. insights: exactly 4, each ONE sentence max 15 words. outlook: exactly 3, each max 10 words. High hashrate/low fees = positive. Reference supply dynamics when available (never "growing scarcity"). progressBars = verbatim from pre-calculated. ALL numbers must come from real data above.`;

const STRUCTURED_PROMPT_OVERVIEW = `Analyze ALL these Bitcoin metrics. Return JSON matching the REQUIRED JSON SCHEMA below.

{metrics}`;

// Fix common AI text garbling — applied to all string values in parsed JSON
const sanitizeAIText = (obj) => {
  if (typeof obj === 'string') {
    return obj
      // Fix tilde appearing inside a number: "~1.~1" → "~1.1", "~1,~100" → "~1,100"
      .replace(/~(\d[\d,.]*?)~(\d)/g, '~$1$2')
      // Strip tilde after a decimal point: "1.~1" → "1.1"
      .replace(/(\d\.)~(\d)/g, '$1$2')
      // Strip tilde after a comma in a number: "1,~100" → "1,100"
      .replace(/(\d,)~(\d)/g, '$1$2')
      // Strip tilde between digits with no separator: "1~1" → "11" (only in numeric context)
      .replace(/(\d)~(\d)/g, '$1$2')
      // Fix any stray double tildes
      .replace(/~~+/g, '~')
      // Final cleanup: tilde should only appear at START of a number, never mid-number
      // Catches any remaining ~digit patterns after a digit: "1.1~2" → "1.12"
      .replace(/(\d[.\d]*)~(\d)/g, '$1$2')
      // Trim excessive decimal places: "5238.97019799 BTC" → "5,239 BTC"
      .replace(/(\d+\.\d{3,})\s*(BTC)/g, (_, num) => `${Math.round(parseFloat(num)).toLocaleString()} BTC`);
  }
  if (Array.isArray(obj)) return obj.map(sanitizeAIText);
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, sanitizeAIText(v)]));
  }
  return obj;
};

// Post-process AI output: thin safety net that corrects formatting/number drift.
// Most semantic correction is handled by the HARD FACTS block in the prompt —
// this layer only enforces canonical labels and patches obvious number drift.
const correctAINumbers = (parsed, canonicalMetrics) => {
  if (!parsed || !canonicalMetrics) return parsed;
  const { raw, formatted } = canonicalMetrics;
  const flags = getCanonicalAIFlags(canonicalMetrics);

  // Note: formatted uses "hashRate" (camelCase) not "hashrate"
  const realHashrate = formatted?.hashRate || (raw.hashrate ? fmt.hashrate(raw.hashrate) : null);
  const realPrice = raw?.price;
  const realPriceFormatted = realPrice
    ? `$${realPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : null;

  const fixText = (text) => {
    if (typeof text !== 'string') return text;

    // Replace any wrong BTC price near price-related context words
    if (realPriceFormatted && realPrice) {
      text = text.replace(
        /(?:bitcoin|btc|price|trading|trades|remains|sits|at|around|near|currently)\b[^.]*?\$[\d,]+(?:\.\d{1,2})?/gi,
        (match) => {
          const priceMatch = match.match(/\$([\d,]+(?:\.\d{1,2})?)/);
          if (priceMatch) {
            const foundPrice = parseFloat(priceMatch[1].replace(/,/g, ''));
            if (foundPrice > 1000 && Math.abs(foundPrice - realPrice) / realPrice > 0.02) {
              return match.replace(/\$[\d,]+(?:\.\d{1,2})?/, realPriceFormatted);
            }
          }
          return match;
        }
      );
    }

    // Strip hallucinated unit suffixes from dollar prices (e.g. "$66,951.00B" → "$66,951.00")
    text = text.replace(/(\$[\d,]+(?:\.\d{1,2})?)[BMTK]\b/g, '$1');

    // Replace any wrong/garbled hashrate (e.g. "~308 EH/s", "~1.~1 ZH/s") with real value
    if (realHashrate) {
      text = text.replace(/~[\d,.~]+\s*[EKZPMT]H\/s/gi, realHashrate);
    }

    return text;
  };

  // Apply fixText recursively to all strings in the parsed object
  const fixObj = (obj) => {
    if (typeof obj === 'string') return fixText(obj);
    if (Array.isArray(obj)) return obj.map(fixObj);
    if (obj && typeof obj === 'object') {
      return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, fixObj(v)]));
    }
    return obj;
  };

  // Force section statuses to match canonical HARD FACTS labels
  if (parsed.sections?.mempool && flags.mempoolState) {
    parsed.sections.mempool.status = flags.mempoolState;
  }
  if (parsed.sections?.mining && flags.miningHealth) {
    parsed.sections.mining.status = flags.miningHealth;
  }

  return fixObj(parsed);
};

// Parse JSON from AI response, handling various formats
const parseAIResponse = (response) => {
  if (!response || typeof response !== 'string') return null;

  let cleaned = response.trim();

  // Remove markdown code blocks if present
  if (cleaned.startsWith('```json')) {
    cleaned = cleaned.slice(7);
  } else if (cleaned.startsWith('```')) {
    cleaned = cleaned.slice(3);
  }
  if (cleaned.endsWith('```')) {
    cleaned = cleaned.slice(0, -3);
  }
  cleaned = cleaned.trim();

  // Try to find JSON object boundaries
  const jsonStart = cleaned.indexOf('{');
  const jsonEnd = cleaned.lastIndexOf('}');

  if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
    cleaned = cleaned.slice(jsonStart, jsonEnd + 1);
  }

  try {
    return sanitizeAIText(JSON.parse(cleaned));
  } catch (e) {
    console.warn('[AIService] Failed to parse JSON response:', e.message);
    return null;
  }
};

// Translate raw API errors into short, human-readable messages
const humanizeProviderError = (msg) => {
  if (!msg) return 'Unknown error';
  const m = msg.toLowerCase();

  // Rate limits — extract the retry time; always offer the free fallback
  if (m.includes('rate limit') || m.includes('rate_limit') || m.includes('too many requests')) {
    const retry = msg.match(/try again in (\d+m[\d.]*s|\d+[\d.]*s)/i);
    const retryStr = retry ? ` Try again in ${retry[1].replace(/(\d+)m[\d.]*s/, '$1 min')}.` : '';
    const useTcs = ' Or use Timechain Stats AI (free).';
    if (m.includes('tokens per day') || m.includes('tpd'))
      return `Daily token limit reached.${retryStr || ' Resets at midnight UTC.'}${useTcs}`;
    if (m.includes('tokens per minute') || m.includes('tpm'))
      return `Per-minute token limit reached.${retryStr || ' Wait ~30 seconds.'}${useTcs}`;
    return `Rate limit reached.${retryStr}${useTcs}`;
  }

  // Model failed to return valid JSON (e.g. Groq json_validate_failed) — usually transient
  if (m.includes('failed to generate json') || m.includes('json_validate') || m.includes('failed_generation'))
    return 'The model returned invalid JSON. Try again, or use Timechain Stats AI (free).';

  // Auth / billing
  if (m.includes('credit balance') || (m.includes('insufficient') && m.includes('credit')))
    return 'Credits depleted. Add funds in your provider dashboard.';
  if (m.includes('invalid') && (m.includes('api key') || m.includes('api_key') || m.includes('auth')))
    return 'Invalid API key. Check your provider settings.';
  if (m.includes('401') || m.includes('unauthorized'))
    return 'Invalid API key.';
  if (m.includes('403') || m.includes('forbidden'))
    return 'Access denied. Check your API key permissions.';

  // Connectivity
  if (m.includes('not found') && m.includes('model'))
    return 'Model not found.';
  if (m.includes('fetch') || m.includes('network') || m.includes('econnrefused'))
    return 'Could not reach provider. Check your connection.';

  // Server errors
  if (m.includes('503') || m.includes('502') || m.includes('overloaded') || m.includes('unavailable'))
    return 'Service temporarily unavailable. Try again shortly.';
  if (m.includes('timeout'))
    return 'Request timed out. Try again.';

  // Unparseable / incomplete
  if (m.includes('unparseable'))
    return 'Provider returned an invalid response.';
  if (m.includes('incomplete json'))
    return 'Provider returned incomplete data.';

  // Fallback: truncate long messages
  return msg.length > 80 ? msg.slice(0, 77) + '...' : msg;
};

// Check if error is a rate limit or should trigger fallback
const isRateLimitError = (error) => {
  const msg = error.message?.toLowerCase() || '';
  return (
    msg.includes('rate limit') ||
    msg.includes('rate_limit') ||
    msg.includes('too many requests') ||
    msg.includes('429') ||
    msg.includes('quota') ||
    msg.includes('exceeded') ||
    msg.includes('capacity') ||
    msg.includes('overloaded') ||
    msg.includes('unavailable') ||
    msg.includes('503') ||
    msg.includes('502') ||
    msg.includes('timeout') ||
    msg.includes('failed to generate json') ||
    msg.includes('json_validate') ||
    msg.includes('failed_generation')
  );
};

// ============================================================================
// CANONICAL METRICS INTEGRATION
// ============================================================================

/**
 * Prepare data sources object from the incoming data structure
 * Handles both old format (data.realtime) and new format (flat sources)
 */
const prepareDataSources = (data) => {
  // Check if data has the old realtime structure
  const rt = data?.realtime || {};

  return {
    data: data,
    bitcoinPrice: rt.bitcoinPrice || data?.bitcoinPrice,
    lastMempoolBlockData: rt.lastMempoolBlockData || data?.lastMempoolBlockData,
    recentBlocks: rt.recentBlocks || data?.recentBlocks || [],
    fees: rt.fees || data?.fees || {},
    mempoolInfo: rt.mempoolInfo || data?.mempoolInfo || {},
    da: rt.da || data?.da || {},
    diamondbackData: rt.diamondbackData || data?.diamondbackData || {},
    blockMetrics: rt.blockMetrics || data?.blockMetrics || {},
    selectedCurrency: data?.selectedCurrency || 'usd'
  };
};

/**
 * Get canonical metrics from data sources
 */
export const getCanonicalMetrics = (data, selectedCurrency = 'usd') => {
  const sources = prepareDataSources(data);
  sources.selectedCurrency = selectedCurrency;

  // Calculate conversion rates
  sources.conversionRates = sources.data ? createCurrencyConverter(sources.data) || {} : {};

  return calculateCanonicalMetrics(sources);
};

/**
 * Format all metrics for AI overview prompt
 * Uses canonical metrics as single source of truth
 */
const formatAllMetricsForOverview = (data, selectedCategories = null) => {
  const metrics = getCanonicalMetrics(data);
  return formatMetricsForAI(metrics, selectedCategories);
};

/**
 * Get pre-calculated data that AI should use verbatim
 */
const getPreCalculatedData = (data) => {
  const metrics = getCanonicalMetrics(data);
  return getPreCalculatedDataForAI(metrics);
};

// ============================================================================
// AI GENERATORS
// ============================================================================

// Generate structured insight with JSON response and fallback support
export const generateStructuredInsight = async (type, data, selectedCategories = null, onProgress = null, signal = null) => {
  const progress = (step, detail) => onProgress?.({ step, detail });

  const validConfigs = getValidConfigs();
  if (validConfigs.length === 0) {
    throw new Error('No AI provider configured. Please add at least one provider.');
  }

  progress('metrics', 'Collecting network metrics...');

  // Get canonical metrics (single source of truth)
  const canonicalMetrics = getCanonicalMetrics(data);

  // Compute analytical metrics (trends, anomalies, correlations)
  const analyticalMetrics = computeAnalyticalMetrics(canonicalMetrics, data);

  progress('analytics', 'Computing analytics & anomalies...');

  // Filter metrics by selected categories to reduce tokens
  const allMetrics = formatAllMetricsForOverview(data, selectedCategories);

  // Pre-calculate progress bars so AI doesn't need to
  const preCalculated = getPreCalculatedData(data);

  // Format analytical data for AI prompt
  const analyticsContext = analyticalMetrics?.analytics
    ? formatAnalyticsForAI(analyticalMetrics.analytics)
    : null;

  const promptTemplate = STRUCTURED_PROMPT_OVERVIEW;
  let prompt = promptTemplate.replace('{metrics}', allMetrics);

  // Inject analytical context FIRST (most important for understanding)
  if (analyticsContext) {
    prompt = `${analyticsContext}\n\n${prompt}`;
  }

  // Add few-shot example for output structure
  prompt += `\n\n${FEW_SHOT_EXAMPLE}`;

  // Focus rotation — shifts the AI's primary analytical angle each hour
  const FOCUS_ANGLES = [
    'miner economics and fee/subsidy dynamics',
    'adoption signals: Lightning, transactions, new addresses',
    'market structure: price vs moving averages, volatility, percentile position',
    'network security: hashrate trends, difficulty, decentralization',
    'fee market: mempool pressure, fee tiers, block fullness',
    'supply narrative: epoch position, halving countdown, inflation rate'
  ];
  const focusIndex = new Date().getHours() % FOCUS_ANGLES.length;
  const focusHint = FOCUS_ANGLES[focusIndex];

  prompt += `\n\nFOCUS_HINT: Lead with "${focusHint}". Weave in other notable observations.`;

  // Category scoping — when the user filtered to a subset, tailor the WHOLE dashboard to it
  const ALL_CATEGORY_KEYS = ['market', 'network', 'mining', 'lightning', 'mempool'];
  if (Array.isArray(selectedCategories) && selectedCategories.length > 0 && selectedCategories.length < ALL_CATEGORY_KEYS.length) {
    prompt += `\n\n=== CATEGORY FOCUS ===\nThe user selected ONLY these categories: ${selectedCategories.join(', ')}. Produce sections, insights, outlook, and topMetrics ONLY for these categories — omit the "sections" keys for every category NOT listed, and do not mention them in insights or outlook. Keep the same output structure and depth for the selected categories.`;
  }

  // Build notable conditions summary from analytics flags
  const flags = analyticalMetrics?.analytics?.flags;
  if (flags) {
    const notable = [];
    if (flags.hasAnomalies) {
      notable.push(`${flags.anomalyCount} anomalies (${flags.criticalAnomalies} critical) — highlight in alerts/insights.`);
    }
    if (flags.trendDirection && flags.trendDirection !== 'UNKNOWN') {
      const strength = flags.trendStrength ? ` (${flags.trendStrength})` : '';
      notable.push(`Price trend: ${flags.trendDirection}${strength}.`);
    }
    if (flags.volatilityLevel === 'HIGH' || flags.volatilityLevel === 'ELEVATED') {
      notable.push(`Volatility: ${flags.volatilityLevel}.`);
    }
    if (flags.networkHealth && flags.networkHealth !== 'HEALTHY') {
      notable.push(`Network health: ${flags.networkHealth}.`);
    }
    if (notable.length > 0) {
      prompt += `\n\n=== NOTABLE CONDITIONS ===\n${notable.join('\n')}`;
    }
  }

  // PRE-CALCULATED VALUES GO LAST — recency bias ensures AI copies these exactly
  if (preCalculated) {
    prompt += `\n\n=== EXACT VALUES — USE VERBATIM ===\n${preCalculated}\nThese are the ONLY correct values for DA, halving, supply, fee/subsidy. Copy exactly — do not round or recalculate.`;
  }

  const generators = {
    openai: generateOpenAIStructured,
    anthropic: generateAnthropicStructured,
    gemini: generateGeminiStructured,
    groq: generateGroqStructured,
    mistral: generateMistralStructured
  };

  // Try providers in priority order (top = highest, fallback on failure)
  const errors = [];
  for (let i = 0; i < validConfigs.length; i++) {
    const config = validConfigs[i];
    const generator = generators[config.provider];
    const providerInfo = AI_PROVIDERS[config.provider];

    if (!generator) {
      errors.push({ provider: config.provider, error: 'Unknown provider' });
      continue;
    }

    try {
      // Resolve 'auto' → cheapest live model (cached; falls back to defaultModel)
      const resolvedModel = await resolveModel(config.provider, config.apiKey, config.endpoint, config.model);
      const activeConfig = { ...config, model: resolvedModel };
      progress('generating', `Querying ${providerInfo?.name || config.provider}...`);
      console.log(`[AIService] Trying provider ${i + 1}/${validConfigs.length}: ${providerInfo?.name || config.provider} (${resolvedModel})`);
      const response = await generator(prompt, activeConfig, signal);
      progress('parsing', 'Parsing response...');
      const parsed = parseAIResponse(response);

      // Validate the parsed response has ALL required fields for a complete dashboard
      const isValidStructured = parsed
        && typeof parsed === 'object'
        && parsed.headline
        && (parsed.overallSentiment || parsed.sentiment)
        && parsed.sections
        && parsed.insights && Array.isArray(parsed.insights) && parsed.insights.length >= 3
        && parsed.outlook && Array.isArray(parsed.outlook) && parsed.outlook.length >= 2
        && parsed.topMetrics && Array.isArray(parsed.topMetrics) && parsed.topMetrics.length >= 4
        && parsed.marketScore;

      const modelName = providerInfo?.fallbackModels?.find(m => m.id === activeConfig.model)?.name || activeConfig.model;

      if (isValidStructured) {
        progress('building', 'Building dashboard...');
        // Correct hallucinated numbers with real canonical data
        const corrected = correctAINumbers(parsed, canonicalMetrics);
        return {
          type: 'structured',
          data: corrected,
          raw: response,
          provider: providerInfo?.name || config.provider,
          model: modelName,
          usedFallback: i > 0,
          strategy: validConfigs.length >= 2 ? 'priority' : 'single',
          analytics: analyticalMetrics?.analytics || null
        };
      }

      // JSON was invalid or missing key fields — try next provider if available
      if (i < validConfigs.length - 1) {
        console.warn(`[AIService] Provider ${providerInfo?.name || config.provider} returned incomplete/invalid JSON, trying next...`);
        errors.push({ provider: providerInfo?.name || config.provider, error: 'Incomplete JSON response' });
        continue;
      }

      // Last provider — return whatever we got rather than nothing
      if (parsed) {
        const corrected = correctAINumbers(parsed, canonicalMetrics);
        return {
          type: 'structured',
          data: corrected,
          raw: response,
          provider: providerInfo?.name || config.provider,
          model: modelName,
          usedFallback: i > 0,
          strategy: validConfigs.length >= 2 ? 'priority' : 'single',
          analytics: analyticalMetrics?.analytics || null
        };
      }

      // Truly unparseable — don't show raw JSON to user, throw instead
      throw new Error(`${providerInfo?.name || config.provider} returned unparseable response`);
    } catch (err) {
      // User-initiated abort: re-throw immediately, do not fall back
      if (err.name === 'AbortError') {
        throw err;
      }

      const errorMsg = err.message || 'Unknown error';
      console.warn(`[AIService] Provider ${providerInfo?.name || config.provider} failed:`, errorMsg);
      errors.push({ provider: providerInfo?.name || config.provider, error: humanizeProviderError(errorMsg) });

      // If it's not a rate limit error and we only have one provider, throw immediately
      if (!isRateLimitError(err) && validConfigs.length === 1) {
        throw new Error(humanizeProviderError(errorMsg));
      }

      // Continue to next provider if rate limited or if there are more providers
      if (i < validConfigs.length - 1) {
        console.log(`[AIService] Falling back to next provider...`);
      }
    }
  }

  // All providers failed — build a clean, readable error
  const errorSummary = errors.map(e => `${e.provider}: ${e.error}`).join('\n');
  throw new Error(`All AI providers failed:\n${errorSummary}`);
};

// Structured generators with JSON-focused system prompts
const generateOpenAIStructured = async (prompt, config, signal = null) => {
  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${config.apiKey}`
    },
    body: JSON.stringify({
      model: config.model || 'gpt-4o-mini',
      messages: [
        { role: 'system', content: STRUCTURED_SYSTEM_PROMPT },
        { role: 'user', content: prompt }
      ],
      max_tokens: 1000,
      temperature: 0.3,
      response_format: { type: 'json_object' }
    }),
    signal
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `OpenAI error: ${response.status}`);
  }

  const responseData = await response.json();
  return responseData.choices[0].message.content;
};

const generateAnthropicStructured = async (prompt, config, signal = null) => {
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config.apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true'
    },
    body: JSON.stringify({
      model: config.model || 'claude-haiku-4-5-20251001',
      max_tokens: 1000,
      // Array-form system with cache_control enables prompt caching (~10x cheaper repeat calls, 5min TTL)
      system: [{
        type: 'text',
        text: STRUCTURED_SYSTEM_PROMPT,
        cache_control: { type: 'ephemeral' }
      }],
      messages: [{ role: 'user', content: prompt }]
    }),
    signal
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `Anthropic error: ${response.status}`);
  }

  const responseData = await response.json();
  return responseData.content[0].text;
};

const generateGeminiStructured = async (prompt, config, signal = null) => {
  const model = config.model || 'gemini-2.0-flash';
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${config.apiKey}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contents: [{ parts: [{ text: `${STRUCTURED_SYSTEM_PROMPT}\n\n${prompt}` }] }],
      generationConfig: {
        maxOutputTokens: 1000,
        temperature: 0.3,
        responseMimeType: 'application/json'
      }
    }),
    signal
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `Gemini error: ${response.status}`);
  }

  const responseData = await response.json();
  return responseData.candidates[0].content.parts[0].text;
};

const generateGroqStructured = async (prompt, config, signal = null) => {
  const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${config.apiKey}`
    },
    body: JSON.stringify({
      model: config.model || 'llama-3.3-70b-versatile',
      messages: [
        { role: 'system', content: STRUCTURED_SYSTEM_PROMPT },
        { role: 'user', content: prompt }
      ],
      max_tokens: 1000,
      temperature: 0.3,
      response_format: { type: 'json_object' }
    }),
    signal
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `Groq error: ${response.status}`);
  }

  const responseData = await response.json();
  return responseData.choices[0].message.content;
};

const generateMistralStructured = async (prompt, config, signal = null) => {
  const response = await fetch('https://api.mistral.ai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${config.apiKey}`
    },
    body: JSON.stringify({
      model: config.model || 'mistral-small-latest',
      messages: [
        { role: 'system', content: STRUCTURED_SYSTEM_PROMPT },
        { role: 'user', content: prompt }
      ],
      max_tokens: 1000,
      temperature: 0.3,
      response_format: { type: 'json_object' }
    }),
    signal
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `Mistral error: ${response.status}`);
  }

  const responseData = await response.json();
  return responseData.choices[0].message.content;
};

// ============================================================================
// EXPORTS
// ============================================================================

// Export raw metrics for mini dashboard (uses canonical metrics)
export const getMetricsSummary = (data) => {
  const metrics = getCanonicalMetrics(data);
  return getCanonicalMetricsSummary(metrics);
};

// Export calculation functions for external use
export { calculateHalvingProgress, calculateDifficultyProgress };

export const testConnection = async (config) => {
  const testPrompt = 'Say "OK" and nothing else.';

  const generators = {
    openai: generateOpenAIStructured,
    anthropic: generateAnthropicStructured,
    gemini: generateGeminiStructured,
    groq: generateGroqStructured,
    mistral: generateMistralStructured
  };

  const generator = generators[config.provider];
  if (!generator) throw new Error(`Unknown provider: ${config.provider}`);

  const resolvedModel = await resolveModel(config.provider, config.apiKey, config.endpoint, config.model);
  try {
    await generator(testPrompt, { ...config, model: resolvedModel });
    return true;
  } catch (err) {
    throw new Error(humanizeProviderError(err.message));
  }
};
